import React, { useState } from 'react';
import { Container, Row, Col, Button, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
    FaDiscord,
    FaMapMarkedAlt,
    FaChartLine,
    FaIdCard,
    FaCity,
    FaHome,
    FaCalendarAlt,
    FaHandshake,
    FaPassport,
    FaLock,
    FaLevelUpAlt,
    FaCrown,
    FaFire,
    FaBriefcase,
    FaTrophy
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import logo from '../assets/logo.png';
import RedirectModal from '../components/RedirectModal';
import SEO from '../components/SEO';
import friendlyServersData from '../data/friendlyServersData.json';

const images = require.context('../assets', false, /\.(png|jpe?g|svg)$/);

const Home = () => {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({
        url: '',
        title: '',
        message: ''
    });

    const handleSocialRedirect = (url, type) => {
        setModalConfig({
            url: url,
            title: <span>{type.toUpperCase()} <span className="text-neon-cyan">REDIRECT</span></span>,
            message: `You are taking a flight to our official ${type} community:`
        });
        setShowModal(true);
    };

    const confirmRedirect = () => {
        window.open(modalConfig.url, '_blank');
        setShowModal(false);
    };

    // Animation Stagger
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };

    // Bento Grid Items
    const features = [
        {
            title: "XP BASED PROGRESSION",
            desc: "Earn XP through active roleplay and daily city activities. Level up your character to unlock city perks, special abilities, and exclusive milestones.",
            icon: FaLevelUpAlt,
            color: "var(--neon-cyan)",
            tag: "LEVEL SYSTEM"
        },
        {
            title: "PLAYER REPUTATION SYSTEM",
            desc: "Your choices define your standing. Build individual reputation across legal, police, and underground criminal syndicates based on your RP history.",
            icon: FaCrown,
            color: "var(--neon-gold)",
            tag: "REPUTATION"
        },
        {
            title: "HUSTLE PASS (SEASON REWARDS)",
            desc: "Progress through seasonal Hustle Pass tiers by completing daily and weekly quests. Unlock exclusive vehicles, custom clothing, and rare rewards.",
            icon: FaFire,
            color: "var(--neon-pink)",
            tag: "SEASON PASS"
        },
        {
            title: "JOB AURA & LEVEL XP",
            desc: "A separate Level and XP progression system for every job! Elevate your Job Aura to command higher salaries, unlock master contracts, and access elite equipment.",
            icon: FaBriefcase,
            color: "#0aff00",
            tag: "JOB AURA"
        },
        {
            title: "BADGES & ACHIEVEMENTS",
            desc: "Complete unique city feats and story milestones to unlock collectible Badges and brag-worthy Achievements displayed on your citizen profile.",
            icon: FaTrophy,
            color: "#bc13fe",
            tag: "ACHIEVEMENTS"
        },
        {
            title: "REAL ECONOMY",
            desc: "A completely player-driven market where every Rupee counts. Run businesses, trade goods, invest, and build your financial empire.",
            icon: FaChartLine,
            color: "var(--neon-gold)",
            tag: "ECONOMY"
        },
        {
            title: "WHITELISTED & PUBLIC JOBS",
            desc: "From PD, EMS, and Mechanics to trucking, fishing, and trade. Choose your career path or build a business from the ground up.",
            icon: FaIdCard,
            color: "var(--neon-cyan)",
            tag: "CAREERS"
        },
        {
            title: "REAL LIFE EVENTS & RP",
            desc: "Spontaneous street meets, server festivals, high-stakes robberies, and deeply immersive Tamil storyline arcs.",
            icon: FaCalendarAlt,
            color: "var(--neon-pink)",
            tag: "EVENTS"
        },
        {
            title: "CUSTOM HOUSING & REAL CITY",
            desc: "Purchase and furnish dream homes. Experience a thriving city with realistic Tamil nameboards, distinct districts, and custom properties.",
            icon: FaHome,
            color: "var(--neon-red)",
            tag: "IMMERSION"
        }
    ];

    return (
        <div style={{ overflowX: 'hidden' }}>
            <SEO
                title="Home | Tamil GTA 5 FiveM Roleplay Server"
                description="Join Thalai Nagaram RP (TNRP) – Tamil Nadu's premier GTA 5 FiveM roleplay server featuring realistic police, EMS, jobs, custom vehicles, events, and a vibrant RP community."
                keywords="Thalai Nagaram RP, TNRP, Tamil RP, Tamil FiveM Server, GTA 5 Tamil RP, FiveM Tamil Nadu, GTA 5 Roleplay Tamil, TNRP GTA 5"
                canonicalUrl="https://thalainagaram.org/"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "Thalai Nagaram RP",
                    "url": "https://thalainagaram.org/",
                    "description": "Tamil GTA 5 FiveM Roleplay Server & Community"
                }}
            />
            {/* --- HERO SECTION --- */}
            <section className="position-relative d-flex align-items-center justify-content-center"
                style={{
                    minHeight: '100vh',

                    perspective: '1000px'
                }}>

                {/* Background Atmosphere - kept similar but intensified for neon */}
                <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%', zIndex: -1 }}>
                    <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: '60vw', height: '60vw', background: 'radial-gradient(circle, rgba(188, 19, 254, 0.15) 0%, transparent 60%)', filter: 'blur(100px)' }} />
                </div>

                <Container fluid>
                    <Row className="align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                        {/* Logo Side (Left on Desktop) */}
                        <Col lg={5} className="text-center mb-5 mb-lg-0">
                            <motion.div
                                variants={itemVariants}
                                className="position-relative d-inline-block"
                                animate={{ y: [0, -20, 0], rotate: [0, 2, -2, 0] }}
                                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <div style={{
                                    position: 'absolute',
                                    top: '50%', left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '300px', height: '300px',
                                    background: 'radial-gradient(circle, rgba(255, 215, 0, 0.2) 0%, transparent 70%)',
                                    filter: 'blur(50px)',
                                    zIndex: -1
                                }} />
                                <img src={logo} alt="TNRP Logo" style={{ width: '100%', maxWidth: '350px', filter: 'drop-shadow(0 0 30px rgba(0,243,255,0.3))' }} />
                            </motion.div>
                        </Col>

                        {/* Text Side (Right on Desktop) */}
                        <Col lg={7} className="text-center text-lg-start ps-lg-5">
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                <motion.h1 variants={itemVariants} className="display-1 fw-bold mb-3" style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', textShadow: '0 0 30px rgba(188, 19, 254, 0.5)', lineHeight: 1.1 }}>
                                    THALAINAGARAM <br /> <span className="text-neon-cyan">ROLEPLAY</span>
                                </motion.h1>

                                <motion.p variants={itemVariants} className="h5 text-secondary mb-3 fw-light text-uppercase tracking-widest" style={{ letterSpacing: '4px', fontSize: '1.2rem', color: '#ccc' }}>
                                    Legacy • Power • <span className="text-neon-pink">Respect</span>
                                </motion.p>

                                {/* Unified Hero Badges & Buttons Container */}
                                <motion.div variants={itemVariants} className="d-flex flex-column align-items-center align-items-lg-start mx-auto mx-lg-0" style={{ maxWidth: '560px' }}>

                                    {/* Feature Quick Badges */}
                                    <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-lg-start mb-4">
                                        <Badge bg="transparent" className="border border-info text-cyan px-3 py-2 fw-bold" style={{ fontSize: '0.72rem', letterSpacing: '1px', boxShadow: '0 0 10px rgba(0,243,255,0.2)' }}>
                                            ⚡ XP PROGRESSION
                                        </Badge>
                                        <Badge bg="transparent" className="border border-warning text-warning px-3 py-2 fw-bold" style={{ fontSize: '0.72rem', letterSpacing: '1px', boxShadow: '0 0 10px rgba(255,215,0,0.2)' }}>
                                            👑 REPUTATION SYSTEM
                                        </Badge>
                                        <Badge bg="transparent" className="border border-danger text-danger px-3 py-2 fw-bold" style={{ fontSize: '0.72rem', letterSpacing: '1px', boxShadow: '0 0 10px rgba(255,0,60,0.2)' }}>
                                            🔥 HUSTLE PASS
                                        </Badge>
                                        <Badge bg="transparent" className="border border-success text-success px-3 py-2 fw-bold" style={{ fontSize: '0.72rem', letterSpacing: '1px', boxShadow: '0 0 10px rgba(10,255,0,0.2)' }}>
                                            💼 JOB AURA & XP
                                        </Badge>
                                        <Badge bg="transparent" className="border border-primary text-primary px-3 py-2 fw-bold" style={{ fontSize: '0.72rem', letterSpacing: '1px', boxShadow: '0 0 10px rgba(188,19,254,0.2)' }}>
                                            🏆 BADGES & FEATS
                                        </Badge>
                                    </div>

                                    {/* Action Buttons 2x2 Grid */}
                                    <div
                                        className="w-100"
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                            gap: '0.9rem'
                                        }}
                                    >
                                        <Button
                                            variant="outline-light"
                                            className="d-flex align-items-center justify-content-center gap-2 px-3 py-3 fw-bold text-nowrap w-100"
                                            style={{ fontSize: '0.9rem', borderColor: '#5865F2', color: '#5865F2', boxShadow: '0 0 20px rgba(88, 101, 242, 0.2)' }}
                                            onClick={() => handleSocialRedirect('https://discord.gg/xpmRWVFZYR', 'Discord')}
                                        >
                                            <FaDiscord size={18} /> JOIN DISCORD
                                        </Button>

                                        <Button
                                            variant="outline-light"
                                            className="d-flex align-items-center justify-content-center gap-2 px-3 py-3 fw-bold text-nowrap w-100"
                                            style={{ fontSize: '0.9rem', borderColor: 'var(--neon-gold)', color: 'var(--neon-gold)', boxShadow: '0 0 20px rgba(255, 215, 0, 0.2)' }}
                                            onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                                        >
                                            <FaCity size={18} /> ABOUT TNRP
                                        </Button>

                                        <Button
                                            variant="outline-light"
                                            className="d-flex align-items-center justify-content-center gap-2 px-3 py-3 fw-bold text-nowrap w-100"
                                            style={{ fontSize: '0.9rem', borderColor: 'var(--neon-cyan)', color: 'var(--neon-cyan)', boxShadow: '0 0 20px rgba(0, 243, 255, 0.2)' }}
                                            onClick={() => navigate('/info?tab=whitelisting', { state: { tab: 'whitelisting' } })}
                                        >
                                            <FaPassport size={18} /> WHITELIST PROCESS
                                        </Button>

                                        <Button
                                            variant="outline-light"
                                            className="d-flex align-items-center justify-content-center gap-2 px-3 py-3 fw-bold text-nowrap w-100"
                                            style={{ fontSize: '0.9rem', borderColor: '#ff003c', color: '#ff003c', boxShadow: '0 0 20px rgba(255, 0, 60, 0.2)' }}
                                            onClick={() => navigate('/info?tab=isolation', { state: { tab: 'isolation' } })}
                                        >
                                            <FaLock size={18} /> ISOLATION PROCESS
                                        </Button>
                                    </div>                                </motion.div>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>

            </section>

            {/* --- ABOUT SECTION --- */}
            <section id="about" className="py-5 position-relative" style={{ background: '#050505', scrollMarginTop: '100px' }}>
                <Container fluid className="text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="display-5 fw-bold mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>WHO WE <span className="text-neon-cyan">ARE</span></h2>
                        <Row className="justify-content-center">
                            <Col lg={8}>
                                <p className="lead text-secondary mb-5" style={{ lineHeight: '1.8' }}>
                                    Thalainagarm, or as our community calls it, <strong className="text-white">TNRP</strong>, is the pioneer when it comes to legacy Tamil RP! We cemented true RP to the Tamil Gaming. We always strive to bring some of the most innovative RP with the most immersive storylines. Join us at TNRP and start your GTA RP journey. It doesn't matter if you are new or a seasoned veteran! We have a story for all! <br />
                                    <span className="text-neon-pink fw-bold">#WeAreTNRP</span>
                                </p>

                                <div className="d-flex justify-content-center gap-3 flex-wrap">
                                    <Button
                                        variant="outline-light"
                                        className="d-inline-flex align-items-center gap-2 px-5 py-3 "
                                        style={{ fontSize: '1.1rem', borderColor: 'var(--neon-gold)', color: 'var(--neon-gold)', boxShadow: '0 0 20px rgba(255, 215, 0, 0.2)' }}
                                        onClick={() => document.getElementById('experience').scrollIntoView({ behavior: 'smooth' })}
                                        whileHover={{ scale: 1.05 }}
                                        as={motion.button}
                                    >
                                        <FaMapMarkedAlt size={20} /> THE EXPERIENCE
                                    </Button>

                                    <Button
                                        variant="outline-light"
                                        className="d-inline-flex align-items-center gap-2 px-5 py-3"
                                        style={{ fontSize: '1.1rem', borderColor: 'var(--neon-cyan)', color: 'var(--neon-cyan)', boxShadow: '0 0 20px rgba(0, 243, 255, 0.2)' }}
                                        onClick={() => document.getElementById('friendly-servers').scrollIntoView({ behavior: 'smooth' })}
                                        whileHover={{ scale: 1.05 }}
                                        as={motion.button}
                                    >
                                        <FaHandshake size={20} /> FRIENDLY SERVERS
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </motion.div>
                </Container>
            </section>

            {/* --- BENTO GRID FEATURES --- */}
            <section id="experience" className="py-3 position-relative" style={{ scrollMarginTop: '100px' }}>
                {/* Decorative Background Grid */}
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                    zIndex: -1,
                    maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)'
                }} />

                <Container fluid>
                    <div className="mb-5 d-flex align-items-end justify-content-between">
                        <div>
                            <h2 className="display-4 fw-bold mb-0">THE <span className="text-neon-gold">EXPERIENCE</span></h2>
                        </div>
                        <div className="d-none d-md-block text-end">
                            <p className="lead text-secondary mb-0" style={{ fontFamily: 'Orbitron, sans-serif' }}>NEXT GEN ROLEPLAY</p>
                        </div>
                    </div>

                    <Row className="g-4 justify-content-center">
                        {/* Map through features for Bento Grid */}
                        {features.map((item, idx) => (
                            <Col lg={4} md={6} key={idx}>
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    className="h-100"
                                >
                                    <div className="h-100 neon-card d-flex flex-column justify-content-between rounded-0" style={{ padding: '1.5rem' }}>

                                        {/* Content */}
                                        <div className="position-relative z-1">
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <div className="d-inline-flex align-items-center justify-content-center"
                                                    style={{ width: '50px', height: '50px', border: `1px solid ${item.color}`, boxShadow: `0 0 15px ${item.color}40`, color: item.color }}>
                                                    <item.icon size={20} />
                                                </div>
                                                {item.tag && (
                                                    <Badge bg="transparent" className="border px-2 py-1 fw-bold" style={{ borderColor: `${item.color}80`, color: item.color, fontSize: '0.65rem', letterSpacing: '1px' }}>
                                                        {item.tag}
                                                    </Badge>
                                                )}
                                            </div>
                                            <h3 className="h5 fw-bold text-white mb-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>{item.title}</h3>
                                            <p className="text-secondary small mb-0" style={{ maxWidth: '100%', color: '#cbd5e1' }}>{item.desc}</p>
                                        </div>

                                        {/* Decorative Tech Lines */}
                                        <div className="position-absolute bottom-0 end-0 p-3 opacity-75">
                                            <div style={{ width: '60px', height: '2px', background: item.color, boxShadow: `0 0 10px ${item.color}` }} />
                                        </div>
                                    </div>
                                </motion.div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>



            {/* --- FRIENDLY SERVERS SECTION --- */}
            <section id="friendly-servers" className="py-5 position-relative" style={{ background: '#080808', scrollMarginTop: '150px' }}>
                <Container fluid>
                    <div className="mb-5 text-center">
                        <h2 className="display-5 fw-bold mb-0">FRIENDLY <span className="text-neon-cyan">SERVERS</span></h2>
                        <p className="text-secondary mt-2">Servers we respect and call friends.</p>
                    </div>

                    <Row className="g-4 justify-content-center">
                        {friendlyServersData.map((server, idx) => {
                            let serverLogo = null;
                            try { serverLogo = images(`./${server.logo}`); } catch (e) { try { serverLogo = images('./logo.png'); } catch (err) { } }

                            return (
                                <Col lg={6} key={idx}>
                                    <motion.div
                                        whileHover={{
                                            boxShadow: `0 0 40px color-mix(in srgb, ${server.color}, transparent 50%)`,
                                            border: `1px solid ${server.color}`
                                        }}
                                        transition={{ duration: 0.3 }}
                                        className="h-100 p-4 neon-card d-flex align-items-center gap-4"
                                        style={{
                                            border: `1px solid color-mix(in srgb, ${server.color}, transparent 60%)`,
                                            borderLeft: `5px solid ${server.color}`,
                                            boxShadow: `0 0 15px color-mix(in srgb, ${server.color}, transparent 80%)`,
                                            background: 'linear-gradient(90deg, rgba(10,10,15,0.95), rgba(0,0,0,0.8))'
                                        }}
                                    >
                                        <div style={{ minWidth: '80px', width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', border: `2px solid ${server.color}`, boxShadow: `0 0 15px ${server.color}40` }}>
                                            <img src={serverLogo} alt={server.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                        <div>
                                            <h4 className="h5 fw-bold mb-2" style={{ fontFamily: 'Orbitron, sans-serif', color: server.color, textShadow: `0 0 10px ${server.color}60` }}>{server.name}</h4>
                                            <p className="text-secondary small mb-0 fst-italic">"{server.message}"</p>
                                        </div>
                                    </motion.div>
                                </Col>
                            );
                        })}
                    </Row>
                </Container>
            </section>

            <RedirectModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmRedirect}
                title={modalConfig.title}
                messageLabel={modalConfig.message}
                targetUrl={modalConfig.url}
                variant="application"
            />
        </div >
    );
};

export default Home;
